import { ChangeDetectionStrategy, Component, TemplateRef, contentChild, input } from '@angular/core';
import { AbstractControl } from '@angular/forms';

import { IconName } from '../icon/icon-name';

/**
 * Declarative step descriptor for `app-wizard-modal`. Renders nothing itself —
 * the wizard reads its inputs for the rail/heading and stamps the projected
 * `<ng-template>` (lazily, active step only) into its content pane.
 *
 *   <app-wizard-step id="basics" [label]="t('basics')" [control]="basicsForm">
 *     <ng-template>…form fields…</ng-template>
 *   </app-wizard-step>
 *
 * Steps are content children of the wizard; wrap one in `@if` to make it
 * conditional — the rail, numbering, and progress react automatically.
 */
@Component({
  selector: 'app-wizard-step',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: '',
})
export class WizardStep {
  /** Stable identity — navigation state survives steps being added/removed. */
  readonly id = input.required<string>();
  /** Rail title (pre-translated, like all wizard-step strings). */
  readonly label = input.required<string>();
  /** Rail subtitle. */
  readonly sublabel = input('');
  /** Marks the step "optional" in the rail (presentation only). */
  readonly optional = input(false);
  /** Icon for the content pane's 40px tile. */
  readonly icon = input<IconName | null>(null);
  /** Content pane heading; falls back to `label`. */
  readonly heading = input('');
  /** Content pane description paragraph. */
  readonly description = input('');
  /** Gates Next while invalid (marked touched on a blocked attempt) and drives
   *  the rail's error state. Omit for steps with nothing to validate. */
  readonly control = input<AbstractControl | null>(null);
  /** Extra synchronous gate for non-form checks; return false to block Next. */
  readonly canLeave = input<(() => boolean) | null>(null);
  /** Step-level error banner (server-side / cross-field message). */
  readonly error = input<string | null>(null);
  /** Per-step override of the Next button label. */
  readonly nextLabel = input<string | null>(null);

  /** Lazy step body; only the active step's template is instantiated. */
  readonly content = contentChild(TemplateRef);
}
